import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Lock, Printer, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from './ui/Toast';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { inputClass, checkboxClass } from './ui/Input';
import { formatDateTime } from '../lib/format';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { useAuthStore } from '../stores/authStore';
import { B2B_ROLES } from '../auth/roles';
import { Permission } from '../auth/permissions';

/**
 * §7h print dialog — template variant, output target, and the field toggles.
 *
 * The toggles start from the owner's default for this document type and are overridable HERE,
 * per print. That is deliberate: the counter decides at the moment of printing whether this
 * particular copy carries weights or comments, and a setting buried in admin config cannot
 * answer that. What was actually produced is recorded on the document.
 *
 * Fields the law pins on are rendered ticked and disabled rather than hidden — an operator who
 * cannot find the "total" checkbox assumes it is missing; one who sees it locked learns why.
 */

type DocType =
  | 'INVOICE'
  | 'QUOTE'
  | 'CREDIT_NOTE'
  | 'DELIVERY_NOTE'
  | 'DELIVERY_NOTE_CLIENT'
  /** Order acknowledgement — the paper a confirmed order produces. */
  | 'ORDER';
type OutputTarget = 'PRINTER' | 'SCREEN' | 'PDF' | 'EMAIL';

interface PrintOptions {
  docType: DocType;
  variants: Array<{ key: DocType; label: string }>;
  outputTargets: OutputTarget[];
  togglable: string[];
  locked: string[];
  defaults: Record<string, boolean>;
}

/** One EMAIL attempt, from document_dispatches — the mail history, successes and bounces alike. */
interface MailRecord {
  id: string;
  recipient: string | null;
  status: 'SENT' | 'FAILED';
  error: string | null;
  created_at: string;
  actor_name: string | null;
}

interface PrintRecord {
  id: string;
  template_key: string;
  output_target: string;
  fields: Record<string, boolean>;
  recipient: string | null;
  created_at: string;
  actor_name: string | null;
}

const fieldLabel = (key: string): string =>
  i18n.t(`print.fieldLabels.${key}`, { defaultValue: key });

const targetLabel = (target: OutputTarget): string =>
  i18n.t(`print.targets.${target}`, { defaultValue: target });

/**
 * One entry of the Template list when the dialog knows its order: a concrete document of that
 * order, printed as one template. A BL appears twice — its client (priced) and picking copies.
 */
type ChoiceKind =
  | 'TICKET'
  | 'DEP'
  | 'INVOICE'
  | 'BL_CLIENT'
  | 'BL'
  | 'ORDER'
  | 'QUOTE'
  | 'CREDIT_NOTE'
  /** Bon de retrait — what the customer comes back with to collect what is still awaited. */
  | 'PICKUP_SLIP';
interface Choice {
  kind: ChoiceKind;
  /** null for a ticket or a pickup slip: no template, no toggles and no print record. */
  docType: DocType | null;
  id: string;
  number: string;
}
const choiceKey = (c: Pick<Choice, 'kind' | 'id'>) => `${c.kind}:${c.id}`;

/**
 * Template order per segment (owner, 2026-10-13): the particulier walks out with a ticket, so
 * Ticket → DEP → Invoice lead; a pro is handed the priced BL, so B2B starts with the BLs. The
 * first document of the order in this order is the one preselected.
 */
const PRIORITY: Record<'B2B' | 'B2C', ChoiceKind[]> = {
  B2C: [
    'TICKET',
    'DEP',
    'INVOICE',
    'ORDER',
    'BL_CLIENT',
    'BL',
    'PICKUP_SLIP',
    'QUOTE',
    'CREDIT_NOTE',
  ],
  B2B: [
    'BL_CLIENT',
    'BL',
    'INVOICE',
    'DEP',
    'ORDER',
    'PICKUP_SLIP',
    'QUOTE',
    'CREDIT_NOTE',
    'TICKET',
  ],
};

/** Which choice a caller's own docType opens on, when it named one. */
const KIND_OF: Record<DocType, ChoiceKind> = {
  ORDER: 'ORDER',
  INVOICE: 'INVOICE',
  QUOTE: 'QUOTE',
  CREDIT_NOTE: 'CREDIT_NOTE',
  DELIVERY_NOTE: 'BL',
  DELIVERY_NOTE_CLIENT: 'BL_CLIENT',
};

/** Fixed-layout documents: their own PDF, no template toggles. */
const FIXED_PDF: Partial<Record<ChoiceKind, (id: string) => string>> = {
  TICKET: (id) => `/receipts/${id}/pdf`,
  PICKUP_SLIP: (id) => `/pickup-slips/${id}/pdf`,
};

/** A pickup slip is printed or opened only; the TICKET goes by e-mail too (feedback 2.5). */
const TICKET_TARGETS: OutputTarget[] = ['PRINTER', 'PDF', 'SCREEN'];
const RECEIPT_TARGETS: OutputTarget[] = ['PRINTER', 'PDF', 'SCREEN', 'EMAIL'];

/** Where the rendered document actually comes from, per family. */
const PDF_URL: Record<DocType, (id: string) => string> = {
  ORDER: (id) => `/orders/${id}/pdf`,
  INVOICE: (id) => `/invoices/${id}/pdf`,
  QUOTE: (id) => `/quotes/${id}/pdf`,
  CREDIT_NOTE: (id) => `/credit-notes/${id}/pdf`,
  DELIVERY_NOTE: (id) => `/delivery-notes/${id}/pdf`,
  DELIVERY_NOTE_CLIENT: (id) => `/delivery-notes/${id}/pdf?variant=client`,
};

export function PrintDocumentDialog({
  docType,
  documentId,
  documentNumber,
  customerId,
  orderId,
  onClose,
  afterSale = false,
  onOpenOrder,
}: {
  docType: DocType;
  documentId: string;
  documentNumber: string;
  customerId?: string | null;
  /** The order this document belongs to: the Template list then offers ALL its documents. */
  orderId?: string | null;
  onClose: () => void;
  /**
   * Opened by the end of a sale (feedback 2.5): the dialog stays open after each send or print,
   * so the seller can e-mail one document AND print another, then close or open the order.
   */
  afterSale?: boolean;
  /** Offers "Open the order" in the footer. */
  onOpenOrder?: () => void;
}) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const ownOrderId = orderId ?? (docType === 'ORDER' ? documentId : null);
  const [current, setCurrent] = useState<Choice>({
    kind: KIND_OF[docType],
    docType,
    id: documentId,
    number: documentNumber,
  });
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [options, setOptions] = useState<PrintOptions | null>(null);
  const [templateKey, setTemplateKey] = useState<DocType>(docType);
  const [target, setTarget] = useState<OutputTarget>('EMAIL');
  const portalRole = useAuthStore((s) => s.user?.role);
  useEffect(() => {
    if (portalRole && B2B_ROLES.includes(portalRole as (typeof B2B_ROLES)[number]))
      setTarget('PRINTER');
  }, [portalRole]);
  /** Once the operator picks a target, switching document no longer resets it. */
  const targetTouched = useRef(false);
  const [fields, setFields] = useState<Record<string, boolean>>({});
  const [recipient, setRecipient] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<PrintRecord[]>([]);
  const [mail, setMail] = useState<MailRecord[]>([]);

  const fixedPdf = FIXED_PDF[current.kind];
  const isTicket = !!fixedPdf;
  const curType = current.docType;
  const curId = current.id;

  /* Every document of the order, ranked for this customer's segment. Read from the same lists the
     order sheet shows (GET /documents?orderId= and GET /receipts?orderId=), so a role that may not
     read a family does not get it offered here either. */
  useEffect(() => {
    if (!ownOrderId) return;
    let alive = true;
    const opened: Choice = {
      kind: KIND_OF[docType],
      docType,
      id: documentId,
      number: documentNumber,
    };
    void Promise.all([
      apiClient
        .get<{
          content: Array<{
            id: string;
            docType: string;
            number: string;
            customerType: string | null;
          }>;
        }>('/documents', { params: { orderId: ownOrderId, size: 100, sort: 'date,asc' } })
        .then((r) => r.data.content ?? [])
        .catch(() => []),
      apiClient
        .get<{
          receipts: Array<{ id: string; receipt_number: string }>;
          depositInvoices: Array<{ id: string; invoice_number: string }>;
        }>('/receipts', { params: { orderId: ownOrderId } })
        .then((r) => r.data)
        .catch(() => ({ receipts: [], depositInvoices: [] })),
    ]).then(([rows, money]) => {
      if (!alive) return;
      const depIds = new Set(money.depositInvoices.map((d) => d.id));
      const found: Choice[] = [
        ...money.receipts.map((r) => ({
          kind: 'TICKET' as const,
          docType: null,
          id: r.id,
          number: r.receipt_number,
        })),
        ...money.depositInvoices.map((d) => ({
          kind: 'DEP' as const,
          docType: 'INVOICE' as const,
          id: d.id,
          number: d.invoice_number,
        })),
      ];
      for (const r of rows) {
        const base = { id: r.id, number: r.number };
        if (r.docType === 'INVOICE' && !depIds.has(r.id)) {
          const dep = r.number.startsWith('DEP');
          found.push({ ...base, kind: dep ? 'DEP' : 'INVOICE', docType: 'INVOICE' });
        } else if (r.docType === 'DELIVERY_NOTE') {
          found.push({ ...base, kind: 'BL_CLIENT', docType: 'DELIVERY_NOTE_CLIENT' });
          found.push({ ...base, kind: 'BL', docType: 'DELIVERY_NOTE' });
        } else if (r.docType === 'PICKUP_SLIP') {
          found.push({ ...base, kind: 'PICKUP_SLIP', docType: null });
        } else if (r.docType === 'ORDER' || r.docType === 'QUOTE' || r.docType === 'CREDIT_NOTE') {
          found.push({ ...base, kind: r.docType, docType: r.docType });
        }
      }
      // The document the dialog was opened on is always offered, even when its family is not
      // in the lists above for this role (a seller's own-sale invoice).
      if (!found.some((c) => choiceKey(c) === choiceKey(opened))) found.push(opened);
      const segment = rows.find((r) => r.customerType)?.customerType === 'B2B' ? 'B2B' : 'B2C';
      const rank = (k: ChoiceKind) => PRIORITY[segment].indexOf(k);
      found.sort((a, b) => rank(a.kind) - rank(b.kind)); // stable: same kind stays by date
      setChoices(found);
      // Opened on the order itself = "print something for this order": start on what this
      // customer is handed first. A specific document someone clicked stays selected.
      if (docType === 'ORDER') setCurrent(found[0]);
    });
    return () => {
      alive = false;
    };
  }, [ownOrderId, docType, documentId, documentNumber]);

  useEffect(() => {
    let alive = true;
    setOptions(null);
    setHistory([]);
    setMail([]);
    if (!curType) {
      // A ticket or a pickup slip has a fixed layout and no toggles. The ticket can be e-mailed
      // (2.5): its address and mail history come from the receipt routes.
      const ticket = current.kind === 'TICKET';
      if (!targetTouched.current || (target === 'EMAIL' && !ticket)) setTarget('PRINTER');
      if (ticket) {
        apiClient
          .get<{ data: { defaultRecipient: string | null; history: MailRecord[] } }>(
            `/receipts/${curId}/email-info`
          )
          .then((r) => {
            if (!alive) return;
            setMail(r.data.data.history ?? []);
            setRecipient((cur) => cur || (r.data.data.defaultRecipient ?? ''));
          })
          .catch(() => alive && setMail([]));
      }
      return () => {
        alive = false;
      };
    }
    const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    apiClient
      .get<{ data: PrintOptions }>(`/documents/print-options/${curType}${qs}`)
      .then((r) => {
        if (!alive) return;
        const o = r.data.data;
        setOptions(o);
        setFields(o.defaults);
        setTemplateKey(o.variants[0]?.key ?? curType);
        setTarget((cur) =>
          !targetTouched.current || !o.outputTargets.includes(cur) ? o.outputTargets[0] : cur
        );
      })
      .catch(showApiError);
    apiClient
      .get<{ data: PrintRecord[] }>(`/documents/print-history/${curType}/${curId}`)
      .then((r) => alive && setHistory(r.data.data ?? []))
      .catch(() => alive && setHistory([]));
    // The customer's address on file, plus everything already emailed for this document. The
    // operator should not have to remember an address the customer record already holds.
    apiClient
      .get<{ data: { defaultRecipient: string | null; history: MailRecord[] } }>(
        `/documents/email-info/${curType}/${curId}`
      )
      .then((r) => {
        if (!alive) return;
        setMail(r.data.data.history ?? []);
        setRecipient((cur) => cur || (r.data.data.defaultRecipient ?? ''));
      })
      .catch(() => alive && setMail([]));
    return () => {
      alive = false;
    };
    // `target` is read only to correct an impossible EMAIL on a ticket — not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curType, curId, customerId, showApiError]);

  const isLocked = (key: string) => options?.locked.includes(key) ?? false;

  /* A seller at the till holds ORDER_VIEW but not the invoice register, so the invoice of THEIR
     sale comes through the order-scoped route; the register route stays for those who have it. */
  const perms = useCapabilitiesStore((s) => s.permissionSet);
  const pdfUrl = (): string =>
    templateKey === 'INVOICE' &&
    !perms.has(Permission.INVOICE_VIEW_ALL) &&
    !perms.has(Permission.INVOICE_VIEW_OWN_COMPANY)
      ? `/invoices/${curId}/order-pdf`
      : PDF_URL[templateKey](curId);

  /** After a send or print: close, or stay for the next document when opened by a sale. */
  const done = () => {
    if (!afterSale) onClose();
  };

  /** Ticket: the receipt's own PDF, opened like the done panel's "Print receipt" — or e-mailed. */
  const printTicket = async () => {
    if (target === 'EMAIL' && !recipient.trim()) {
      push({ tone: 'warning', message: t('print.recipientRequired') });
      return;
    }
    setBusy(true);
    try {
      if (target === 'EMAIL') {
        const { data } = await apiClient.post<{ data: { sentTo: string } }>(
          `/receipts/${curId}/email`,
          { recipient: recipient.trim() },
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        push({ tone: 'success', message: t('print.emailed', { recipient: data.data.sentTo }) });
        const r = await apiClient
          .get<{ data: { history: MailRecord[] } }>(`/receipts/${curId}/email-info`)
          .catch(() => null);
        if (r) setMail(r.data.data.history ?? []);
        done();
        return;
      }
      const res = await apiClient.get(fixedPdf!(curId), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      done();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const ready = (!ownOrderId || choices !== null) && (isTicket || options !== null);
  /* Feedback 3.10 — a portal login prints and downloads; it never e-mails a document from our
     system to an address of its choosing. The server strips E-mail from the options and refuses
     it on send; this keeps the screen honest even for the ticket's fixed list. */
  const portal = B2B_ROLES.includes(portalRole as (typeof B2B_ROLES)[number]);
  const targets: OutputTarget[] = (
    isTicket
      ? current.kind === 'TICKET'
        ? RECEIPT_TARGETS
        : TICKET_TARGETS
      : (options?.outputTargets ?? [])
  ).filter((ot) => !(portal && ot === 'EMAIL'));

  const submit = async () => {
    if (isTicket) return printTicket();
    if (target === 'EMAIL' && !recipient.trim()) {
      push({ tone: 'warning', message: t('print.recipientRequired') });
      return;
    }
    setBusy(true);
    try {
      /* Render BEFORE recording: a refused or failed render used to leave a "printed" row in the
         print history for a document nobody ever saw. */
      const opensPdf = target === 'PRINTER' || target === 'SCREEN' || target === 'PDF';
      const blob = opensPdf
        ? ((await apiClient.get(pdfUrl(), { responseType: 'blob' })).data as Blob)
        : null;
      // Record next: the server re-resolves the toggles through the legal floor and hands back
      // what will actually be printed, so the record can never claim a field the renderer
      // would have overridden.
      const { data } = await apiClient.post<{
        data: { fields: Record<string, boolean>; sentTo?: string };
      }>(
        '/documents/print',
        {
          docType: curType,
          documentId: curId,
          templateKey,
          outputTarget: target,
          fields,
          customerId: customerId ?? undefined,
          recipient: target === 'EMAIL' ? recipient.trim() : undefined,
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setFields(data.data.fields);

      if (opensPdf && blob) {
        /* One document, two entry points: this opens the SAME renderer the automatic
           DELIVERED email uses, rather than a second rendering path that could drift.

           FETCHED, NOT LINKED. This used to hand the path straight to window.open as
           `/api${...}`, which was wrong twice over. The API is mounted at `/api/v1`
           (API_PREFIX), so the URL 404'd — `/api/orders/<id>/pdf` reached Express and matched
           nothing. And even spelled correctly it could not have worked: the PDF routes are
           behind `requireAuth`, which reads the Bearer token from the Authorization header,
           and a window.open navigation sends no headers — it would have 401'd instead.

           So go through apiClient like every other call here: it owns the base URL and the
           token, and the blob is what gets opened. Same shape as OrderDetail's delivery-note
           print. The object URL is released on a timer because revoking it immediately would
           pull the document out from under the tab that is still loading it. */
        const url = URL.createObjectURL(blob as Blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
      push({
        tone: 'success',
        message:
          target === 'EMAIL'
            ? t('print.emailed', { recipient: data.data.sentTo ?? recipient.trim() })
            : t('print.recorded', {
                number: current.number,
                target: targetLabel(target).toLowerCase(),
              }),
      });
      if (target === 'EMAIL') {
        const r = await apiClient
          .get<{ data: { history: MailRecord[] } }>(`/documents/email-info/${curType}/${curId}`)
          .catch(() => null);
        if (r) setMail(r.data.data.history ?? []);
      }
      done();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-lg bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {t('print.heading', { number: current.number })}
            </h2>
            <p className="text-xs text-muted-foreground">{t('print.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label={t('print.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {!ready ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('print.template')}
                  </span>
                  {choices ? (
                    <Select
                      value={choiceKey(current)}
                      onChange={(e) => {
                        const next = choices.find((c) => choiceKey(c) === e.target.value);
                        if (next) setCurrent(next);
                      }}
                    >
                      {choices.map((c) => (
                        <option key={choiceKey(c)} value={choiceKey(c)}>
                          {t(`print.choices.${c.kind}`)} — {c.number}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Select
                      value={templateKey}
                      onChange={(e) => setTemplateKey(e.target.value as DocType)}
                    >
                      {options?.variants.map((v) => (
                        <option key={v.key} value={v.key}>
                          {v.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('print.sendTo')}
                  </span>
                  <Select
                    value={target}
                    onChange={(e) => {
                      targetTouched.current = true;
                      setTarget(e.target.value as OutputTarget);
                    }}
                  >
                    {targets.map((ot) => (
                      <option key={ot} value={ot}>
                        {targetLabel(ot)}
                      </option>
                    ))}
                  </Select>
                </label>
              </div>

              {target === 'EMAIL' && (
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('print.recipient')}
                  </span>
                  <input
                    className={inputClass}
                    type="email"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder={t('print.recipientPlaceholder')}
                  />
                </label>
              )}

              {isTicket && <p className="text-xs text-muted-foreground">{t('print.ticketNote')}</p>}

              {options && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold">{t('print.fields')}</h3>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    {[...options.togglable, ...options.locked].map((key) => {
                      const locked = isLocked(key);
                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-2 rounded border px-2 py-1.5 text-sm ${
                            locked ? 'bg-muted/50 text-muted-foreground' : ''
                          }`}
                          title={locked ? t('print.lockedTitle') : undefined}
                        >
                          <input
                            type="checkbox"
                            className={checkboxClass}
                            checked={locked ? true : (fields[key] ?? false)}
                            disabled={locked}
                            onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.checked }))}
                          />
                          {fieldLabel(key)}
                          {locked && <Lock className="ml-auto h-3 w-3" />}
                        </label>
                      );
                    })}
                  </div>
                  {options.locked.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">{t('print.lockedNote')}</p>
                  )}
                </div>
              )}

              {mail.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold">{t('print.alreadyEmailed')}</h3>
                  <ul className="space-y-1 text-xs">
                    {mail.slice(0, 6).map((m) => (
                      <li key={m.id} className="flex flex-wrap gap-x-2">
                        <span className="text-muted-foreground">
                          {formatDateTime(m.created_at)}
                        </span>
                        <span
                          className={m.status === 'SENT' ? 'text-emerald-600' : 'text-destructive'}
                        >
                          · {m.status === 'SENT' ? t('print.sent') : t('print.failed')}
                        </span>
                        {m.recipient && (
                          <span className="text-muted-foreground">· {m.recipient}</span>
                        )}
                        {m.actor_name && (
                          <span className="text-muted-foreground">· {m.actor_name}</span>
                        )}
                        {/* A bounce is the row that matters most — never hide its reason. */}
                        {m.error && <span className="w-full text-destructive">{m.error}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {history.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold">{t('print.alreadyPrinted')}</h3>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {history.slice(0, 5).map((h) => (
                      <li key={h.id} className="flex flex-wrap gap-x-2">
                        <span>{formatDateTime(h.created_at)}</span>
                        <span>· {targetLabel(h.output_target as OutputTarget)}</span>
                        <span>· {h.template_key}</span>
                        {h.actor_name && <span>· {h.actor_name}</span>}
                        {h.recipient && <span>· {h.recipient}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t px-5 py-3">
          {onOpenOrder && (
            <Button variant="ghost" size="sm" className="mr-auto" onClick={onOpenOrder}>
              {t('print.openOrder')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            {afterSale ? t('print.close') : t('print.cancel')}
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={busy || !ready}>
            <Printer className="h-4 w-4" />
            {busy
              ? t('print.working')
              : target === 'EMAIL'
                ? t('print.sendEmail')
                : t('print.print')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
